import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import { LoadingSpinner } from '../../ui/Loading';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/Table';
import { NumberedPagination, useTimezone } from '@kioskgaming/ui';
import { useAdminAgentPaymentTransactions } from '../../../hooks/useApi';
import { usePermission } from '../../../hooks/useAuth';
import type { AdminAgentPaymentTransactionRow } from '../../../types';
import {
  AGENT_TX_PAGE_SIZE,
  formatAgentTxDate,
  formatAgentPaymentMoney,
  formatAgentCreditsReceived,
  getAgentPaymentPaidAndCredits,
} from './agentCdnTxUtils';
import { cn } from '../../../utils';

function MoneyFlowCell({
  amountLabel,
  unit,
  direction,
}: {
  amountLabel: string;
  unit: string;
  direction: 'in' | 'out';
}) {
  const isIn = direction === 'in';
  return (
    <div className="inline-flex flex-col items-end gap-0.5">
      <span
        className={cn(
          'font-semibold tabular-nums text-sm whitespace-nowrap',
          isIn ? 'text-emerald-700' : 'text-red-700',
        )}
      >
        {isIn ? '+' : '−'}
        {amountLabel}
      </span>
      <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{unit}</span>
    </div>
  );
}

export type AgentPaymentTransactionsPanelProps = {
  agentId: string;
  /** When false, queries stay idle (e.g. tab not visible). */
  queryEnabled?: boolean;
  /** Shown above the table on the standalone agent payment page. */
  showAdminTransactionsLink?: boolean;
};

export const AgentPaymentTransactionsPanel: React.FC<AgentPaymentTransactionsPanelProps> = ({
  agentId,
  queryEnabled = true,
  showAdminTransactionsLink = false,
}) => {
  const { timezone } = useTimezone();
  const canManage = usePermission('agentManagement');
  const canTransactionMgmt = usePermission('transactionManagement');
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [detailRow, setDetailRow] = useState<AdminAgentPaymentTransactionRow | null>(null);

  useEffect(() => {
    setPage(1);
  }, [statusFilter]);

  const params = useMemo(
    () => ({
      page,
      limit: AGENT_TX_PAGE_SIZE,
      ...(statusFilter ? { status: statusFilter } : {}),
    }),
    [page, statusFilter],
  );

  const fetchEnabled = !!agentId && canManage && queryEnabled;
  const query = useAdminAgentPaymentTransactions(agentId, fetchEnabled, params);
  const filteredRows = useMemo(() => {
    const rows = query.data?.data?.transactions ?? [];
    const keyword = searchTerm.trim().toLowerCase();
    if (!keyword) return rows;
    return rows.filter((row) => {
      const idMatch = String(row.id ?? '').toLowerCase().includes(keyword);
      const publicIdMatch = String(row.transactionId ?? '').toLowerCase().includes(keyword);
      return idMatch || publicIdMatch;
    });
  }, [query.data?.data?.transactions, searchTerm]);

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between space-y-0">
        <div>
          <CardTitle className="text-base">Transactions</CardTitle>
          <p className="text-xs text-muted-foreground font-normal mt-1 max-w-2xl">
            <strong>Amount paid (−)</strong>: fiat/cash charged at the cost rate.{' '}
            <strong>Credits received (+)</strong>: CDN credits added to the wallet.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-sm text-muted-foreground whitespace-nowrap" htmlFor={`pay-status-filter-${agentId}`}>
            Status
          </label>
          <select
            id={`pay-status-filter-${agentId}`}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="processing">Processing</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
            <option value="cancelled">Cancelled</option>
            <option value="expired">Expired</option>
          </select>
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by ID"
            className="h-9 w-44"
          />
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {showAdminTransactionsLink && canTransactionMgmt && (
          <p className="px-6 pb-2 text-xs text-muted-foreground">
            Full admin tools:{' '}
            <Link to="/transactions" className="font-medium text-primary hover:underline">
              Payment transactions
            </Link>
            . Use numeric ID below for lookup.
          </p>
        )}
        {query.isLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : query.error ? (
          <p className="px-6 py-8 text-sm text-destructive">Failed to load payment transactions.</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="whitespace-nowrap w-16">ID</TableHead>
                    <TableHead className="whitespace-nowrap">Date</TableHead>
                    <TableHead>Via</TableHead>
                    <TableHead className="text-right whitespace-nowrap" title="Charged amount (after purchase discount)">
                      Amount paid
                    </TableHead>
                    <TableHead className="text-right whitespace-nowrap" title="CDN credits credited to wallet">
                      Credits received
                    </TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                        No payment transactions found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredRows.map((row: AdminAgentPaymentTransactionRow) => {
                      const pair = getAgentPaymentPaidAndCredits(row);
                      const cur = row.currency ?? 'USD';
                      const via = [row.provider, row.paymentMethod].filter(Boolean).join(' · ') || '—';
                      return (
                        <TableRow
                          key={row.id}
                          className="cursor-pointer"
                          onClick={() => setDetailRow(row)}
                        >
                          <TableCell className="font-mono text-xs text-muted-foreground">{row.id ?? '—'}</TableCell>
                          <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                            {formatAgentTxDate(row.createdAt, timezone)}
                          </TableCell>
                          <TableCell className="text-sm">{via}</TableCell>
                          <TableCell className="text-right">
                            {pair ? (
                              <MoneyFlowCell
                                amountLabel={formatAgentPaymentMoney(pair.paid, cur)}
                                unit={(cur || 'USD').toString().toUpperCase()}
                                direction="out"
                              />
                            ) : (
                              <span className="text-muted-foreground text-sm">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {pair ? (
                              <MoneyFlowCell
                                amountLabel={formatAgentCreditsReceived(pair.creditsReceived)}
                                unit="Credits"
                                direction="in"
                              />
                            ) : (
                              <span className="text-muted-foreground text-sm">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <span
                              className={cn(
                                'inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide',
                                row.status === 'completed' && 'bg-emerald-100 text-emerald-700',
                                row.status === 'pending' && 'bg-amber-100 text-amber-700',
                                row.status === 'failed' && 'bg-red-100 text-red-700',
                                row.status !== 'completed' && row.status !== 'pending' && row.status !== 'failed' && 'bg-muted text-muted-foreground'
                              )}
                            >
                              {row.status ?? '—'}
                            </span>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 border-t px-4 py-3 text-sm text-muted-foreground">
              <span>
                Page {query.data?.data?.pagination.page ?? 1} of {query.data?.data?.pagination.totalPages ?? 1} (
                {query.data?.data?.pagination.total ?? 0} total)
              </span>
              <NumberedPagination
                currentPage={page}
                totalPages={query.data?.data?.pagination.totalPages ?? 1}
                onPageChange={setPage}
                disabled={query.isFetching}
                variant="admin"
              />
            </div>
          </>
        )}
      </CardContent>
      {detailRow && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="payment-detail-title"
          onClick={() => setDetailRow(null)}
        >
          <div className="w-full max-w-xl" onClick={(e) => e.stopPropagation()}>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle id="payment-detail-title" className="text-base">
                  Transaction detail
                </CardTitle>
                <Button type="button" variant="ghost" size="sm" onClick={() => setDetailRow(null)}>
                  <X className="h-4 w-4" />
                </Button>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <p><span className="text-muted-foreground">ID:</span> <span className="font-mono">{detailRow.id ?? '—'}</span></p>
                <p><span className="text-muted-foreground">Public ID:</span> <span className="font-mono break-all">{detailRow.transactionId || '—'}</span></p>
                <p><span className="text-muted-foreground">Date:</span> {formatAgentTxDate(detailRow.createdAt, timezone)}</p>
                <p><span className="text-muted-foreground">Provider:</span> {detailRow.provider || '—'}</p>
                <p><span className="text-muted-foreground">Method:</span> {detailRow.paymentMethod || '—'}</p>
                <p><span className="text-muted-foreground">Status:</span> {detailRow.status || '—'}</p>
                {(() => {
                  const pair = getAgentPaymentPaidAndCredits(detailRow);
                  const cur = (detailRow.currency ?? 'USD').toString().toUpperCase();
                  if (!pair) return null;
                  return (
                    <>
                      <p>
                        <span className="text-muted-foreground">Amount paid (− · {cur}):</span>{' '}
                        <span className="font-semibold text-red-700 tabular-nums">
                          −{formatAgentPaymentMoney(pair.paid, cur)}
                        </span>
                      </p>
                      <p>
                        <span className="text-muted-foreground">Credits received (+ · Credits):</span>{' '}
                        <span className="font-semibold text-emerald-700 tabular-nums">
                          +{formatAgentCreditsReceived(pair.creditsReceived)}
                        </span>
                      </p>
                    </>
                  );
                })()}
                <p><span className="text-muted-foreground">Description:</span> {detailRow.description || '—'}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </Card>
  );
};
